import React, { useEffect, useState } from "react";
import { apiGet, apiPost, apiPut, apiDelete } from "../../utils/api";
import { FaUserPlus, FaTrash, FaCopy } from "react-icons/fa";
import { Spinner, Badge } from "../../components/ui";
import { useModal } from "../../context/ModalContext";
import { useAuth } from "../../context/AuthContext";

export default function TeamTab() {
  const modal = useModal();
  const { teamRole } = useAuth();
  const isTeamMember = !!teamRole; // user is on someone else's team
  const [members, setMembers] = useState([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [loading, setLoading] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [copiedLink, setCopiedLink] = useState(null);
  const [workspaceId, setWorkspaceId] = useState("");

  useEffect(() => {
    if (!isTeamMember) fetchMembers();
  }, []);

  const fetchMembers = () => {
    setLoading(true);
    apiGet("/api/settings/team")
      .then((res) => {
        setMembers(res?.members || []);
        setWorkspaceId(res?.workspace?.id || "");
      })
      .catch(() => setMembers([]))
      .finally(() => setLoading(false));
  };

  const inviteMember = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    setInviting(true);
    try {
      const res = await apiPost("/api/settings/team/invite", { email: email.trim(), role });
      if (res?.success) {
        modal.alert(
          res.emailSent
            ? "Invite sent via email!"
            : "Invite created. Share the link with your team member.",
          "success"
        );
        // Copy invite link to clipboard
        if (res.inviteLink) {
          try {
            await navigator.clipboard.writeText(res.inviteLink);
            setCopiedLink(res.inviteLink);
            setTimeout(() => setCopiedLink(null), 5000);
          } catch {}
        }
        setEmail("");
        setRole("member");
        fetchMembers();
      } else {
        modal.alert(res?.message || "Failed to send invite", "error");
      }
    } catch (err) {
      modal.alert(err?.message || "Failed to send invite", "error");
    } finally {
      setInviting(false);
    }
  };

  const updateRole = async (memberId, newRole) => {
    const res = await apiPut(`/api/settings/team/${memberId}/role`, { role: newRole });
    if (res?.success) fetchMembers();
    else modal.alert(res?.message || "Failed to update role", "error");
  };

  const removeMember = async (memberId, label, isInvite = false) => {
    const targetLabel = label || (isInvite ? "this invite" : "this member");
    const actionLabel = isInvite ? "Remove invite" : "Remove";
    if (!(await modal.confirm(`${actionLabel} ${targetLabel}?`, { danger: true, confirmText: actionLabel }))) return;
    const params = new URLSearchParams();
    if (workspaceId) params.set("workspaceId", workspaceId);
    const selected = members.find((m) => m._id === memberId);
    if (selected?.email) params.set("email", selected.email);
    const suffix = params.toString() ? `?${params.toString()}` : "";
    const res = await apiDelete(`/api/settings/team/${memberId}${suffix}`);
    if (res?.success) {
      modal.alert(res?.message || (isInvite ? "Invite removed" : "Member removed"), "success");
      fetchMembers();
    }
  };

  const leaveTeam = async () => {
    if (!(await modal.confirm("Are you sure you want to leave this team?", { danger: true, confirmText: "Leave Team" }))) return;
    const res = await apiPost("/api/settings/team/leave");
    if (res?.success) {
      modal.alert("You have left the team. Please log in again to refresh your session.", "success");
    }
  };

  // If user is a team member (not owner), show limited view
  if (isTeamMember) {
    return (
      <div>
        <h2 className="text-lg font-semibold text-ink-primary mb-5">Team</h2>
        <div className="p-4 rounded-lg border border-surface-border max-w-md">
          <p className="text-sm text-ink-secondary mb-1">Your role</p>
          <Badge variant={teamRole === "admin" ? "brand" : "info"} className="capitalize">
            {teamRole}
          </Badge>
          <p className="text-sm text-ink-muted mt-3">
            You are a member of this workspace.
            {teamRole === "member" && " Some actions like billing and session deletion are restricted."}
          </p>
        </div>
        <button className="btn-danger mt-4" onClick={leaveTeam}>
          Leave Team
        </button>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-lg font-semibold text-ink-primary mb-5">Team Members</h2>

      <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6">
        {/* Left column: invite + role legend */}
        <div className="space-y-4">
          {/* Invite form */}
          <form onSubmit={inviteMember} className="p-4 rounded-xl border border-surface-border bg-surface-card">
            <div className="input-group">
              <label className="input-label">Email address</label>
              <input
                type="email"
                className="input"
                placeholder="colleague@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="input-group mt-3">
              <label className="input-label">Role</label>
              <select
                className="input"
                value={role}
                onChange={(e) => setRole(e.target.value)}
              >
                <option value="member">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <button type="submit" className="btn-primary w-full mt-4 flex items-center justify-center gap-1.5" disabled={inviting}>
              {inviting ? <Spinner size="sm" /> : <FaUserPlus size={13} />}
              Send Invite
            </button>
            <p className="text-xs text-ink-muted mt-2 text-center">
              An email with a secure invite link will be sent to this address.
            </p>
          </form>

          {/* Copied link banner */}
          {copiedLink && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-brand-50 border border-brand/20">
              <FaCopy size={12} className="text-brand" />
              <span className="text-sm text-brand-700">Invite link copied to clipboard</span>
            </div>
          )}

          {/* Role description */}
          <div className="space-y-2">
            <div className="p-3 rounded-lg bg-surface-bg border border-surface-border">
              <p className="text-sm font-medium text-ink-primary mb-1">Admin</p>
              <p className="text-xs text-ink-muted">Full access including billing, plan management, and session deletion.</p>
            </div>
            <div className="p-3 rounded-lg bg-surface-bg border border-surface-border">
              <p className="text-sm font-medium text-ink-primary mb-1">User</p>
              <p className="text-xs text-ink-muted">Can manage connections and send messages. Cannot access billing or delete sessions.</p>
            </div>
          </div>
        </div>

        {/* Right column: members list */}
        <div>
          {loading ? (
            <div className="flex items-center gap-2 text-ink-muted"><Spinner size="sm" /> Loading members...</div>
          ) : members.length === 0 ? (
            <div className="p-6 rounded-xl border border-dashed border-surface-border text-center">
              <p className="text-sm text-ink-muted">No team members yet. Invite someone to get started.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {members.map((m) => (
                <div
                  key={m._id}
                  className="flex items-center justify-between p-3 rounded-lg border border-surface-border bg-surface-card"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-ink-primary truncate">
                        {m.name || m.email}
                      </span>
                      <Badge variant={m.status === "accepted" ? "success" : m.status === "pending" ? "warning" : "danger"}>
                        {m.status}
                      </Badge>
                    </div>
                    {m.name && (
                      <div className="text-xs text-ink-muted mt-0.5">{m.email}</div>
                    )}
                  </div>

                  <div className="flex items-center gap-2 ml-3">
                    <select
                      className="input text-xs py-1 px-2 w-24"
                      value={m.role}
                      onChange={(e) => updateRole(m._id, e.target.value)}
                    >
                      <option value="member">User</option>
                      <option value="admin">Admin</option>
                    </select>
                    <button
                      className="btn-danger btn-sm btn-icon"
                      onClick={() => removeMember(m._id, m.name || m.email, m.status === "pending")}
                      title={m.status === "pending" ? "Remove invite" : "Remove member"}
                    >
                      <FaTrash size={12} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
